import { useAtom } from 'jotai';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { BillingTrialStatus } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { MAX_INVITE_EMAILS } from '@gitbook/app-database';
import { PaletteCommandType, type PaletteGroupDef } from '@gitbook/react-uikit';
import { Button, intlPluralizeString, toast } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { apiClient, useTrial } from '../../../state';
import { BulkInviteLimitAlert, PermissionRoleButton } from '../../Organization';
import { UserTagInput, type UserTagInputRef } from '../../Users';
import type { ShareContentModalProps } from '../types';
import {
    type EmailTag,
    inviteMembersInputAtom,
    type PermissionTag,
    type TeamTag,
    type UserTag,
} from './state';

/**
 * Input to create new permissions by searching users, teams or emails
 */
export function InviteMembersInput(props: ShareContentModalProps): React.ReactElement {
    const { content, organization } = props;

    // Under enforced SSO, people join through the IdP, so only existing members can be invited.
    const canAddGuests = organization.permissions.admin && !organization.sso;

    const trial = useTrial({ organization });
    const trialAvailable = trial.status === BillingTrialStatus.None;

    const [tags, setTags] = useAtom(inviteMembersInputAtom);

    const [permission, setPermission] = React.useState<api.MemberRoleOrGuest>(api.MemberRole.Edit);
    const [inviteAsOrgMember, setInviteAsOrgMember] = React.useState(false);
    const [loading, wrapLoading] = useLoadingState();
    const tagInputRef = React.useRef<UserTagInputRef>(null);

    const extraPaletteGroups = () => {
        return [
            {
                key: 'guest-member',
                commands: [
                    {
                        key: 'guest-member.option',
                        title: 'Invite as organization member',
                        subtitle: 'grants selected role org-wide, not just this content',
                        type: PaletteCommandType.SWITCH,
                        enabled: inviteAsOrgMember,
                        trigger: () => {
                            setInviteAsOrgMember((prev) => !prev);
                        },
                    },
                ],
            },
        ] satisfies PaletteGroupDef[];
    };

    const reset = () => {
        tagInputRef.current?.reset();
        setTags([]);
    };

    const { emails, users, teams } = React.useMemo(() => {
        return {
            emails: tags.filter(
                ((tag) => tag.type === 'email') as (tag: PermissionTag) => tag is EmailTag
            ),
            users: tags.filter(
                ((tag) => tag.type === 'user') as (tag: PermissionTag) => tag is UserTag
            ),
            teams: tags.filter(
                ((tag) => tag.type === 'team') as (tag: PermissionTag) => tag is TeamTag
            ),
        };
    }, [tags]);

    //
    // User submit
    //
    const onSubmit = () => {
        if (emails.length > MAX_INVITE_EMAILS) {
            toast.error('Error while updating the permissions', null, {
                description: `You can only invite up to ${MAX_INVITE_EMAILS} emails at once.`,
            });
            return;
        }

        void wrapLoading(async () => {
            let usersToAdd = users.map((u) => u.user);
            let guestsCount = 0;

            // Convert emails to users
            if (emails.length > 0) {
                await toast.catch(async () => {
                    const { data } = await apiClient.organizationInvites.inviteUsersToOrganization(
                        organization.id,
                        {
                            emails: emails.map((e) => e.email),
                            role: inviteAsOrgMember ? permission : null,
                        }
                    );

                    usersToAdd = usersToAdd.concat(...data.users);
                    if (!inviteAsOrgMember) {
                        guestsCount = data.invited;
                    }
                }, 'Error while inviting users');
            }

            // Create permissions
            await toast.catch(async () => {
                if ('space' in content) {
                    await apiClient.spacePermissions.inviteToSpace(content.space.id, {
                        users: usersToAdd,
                        teams: teams.map((team) => team.team),
                        role: permission,
                    });
                }
                if ('collection' in content) {
                    await apiClient.collectionPermissions.inviteToCollection(
                        content.collection.id,
                        {
                            users: usersToAdd,
                            teams: teams.map((team) => team.team),
                            role: permission,
                        }
                    );
                }
            }, 'Error while inviting users');

            const usersCount = usersToAdd.length - guestsCount;

            toast.success(
                `${intlPluralizeString({
                    count: usersCount,
                    singular: 'Invite',
                    plural: 'Invites',
                })} sent`
            );

            reset();

            if ('space' in content) {
                analytics.logEvent(
                    analytics.createSpaceEvent(content.space, {
                        type: 'space_edit_permissions',
                        teamsAdded: teams.length,
                        usersAdded: usersCount,
                        guestsAdded: guestsCount,
                        permissionLevel: permission,
                    })
                );
            } else if ('collection' in content) {
                analytics.logEvent(
                    analytics.createCollectionEvent(content.collection, {
                        type: 'collection_edit_permissions',
                        teamsAdded: teams.length,
                        usersAdded: usersCount,
                        guestsAdded: guestsCount,
                        permissionLevel: permission,
                    })
                );
            }
        });
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center">
                <UserTagInput
                    ref={tagInputRef}
                    organization={organization.id}
                    canInviteUsers={canAddGuests}
                    value={tags}
                    onChangeValue={setTags}
                    disabled={loading}
                    button={
                        <PermissionRoleButton
                            size="small"
                            kind="blank"
                            value={permission}
                            onValueChange={setPermission}
                            icon={null}
                            extraPaletteGroups={canAddGuests ? extraPaletteGroups : undefined}
                        />
                    }
                />

                <Button
                    kind={
                        trialAvailable
                            ? 'upgrade'
                            : loading || tags.length === 0
                              ? 'secondary'
                              : 'primary'
                    }
                    size="large"
                    className="ml-2"
                    loadable={[loading]}
                    disabled={loading || tags.length === 0}
                    onPress={onSubmit}
                    label={trialAvailable ? 'Invite & start trial' : 'Invite'}
                />
            </div>

            <BulkInviteLimitAlert emailCount={emails.length} />
        </div>
    );
}
